/**
 * Who may use this system at all — REQUIREMENTS.md §9, §12.1.
 *
 * Enabling Google sign-in lets *any* Google account authenticate against this
 * project, so authentication is not authorization. `firestore.rules` and
 * `storage.rules` are the gate on stored data; a Cloud Function runs with the
 * Admin SDK and **bypasses those rules entirely**, which is why the same
 * decision has to be reachable from code.
 *
 * The list is no longer hardcoded. The owner is, and everyone else lives in
 * the `allowedUsers` Firestore collection, so adding a colleague is a write
 * from the admin panel rather than an edit to three files and a deploy.
 *
 * **The owner stays in code on purpose.** A purely data-driven allowlist can
 * lock everyone out of its own repair — empty the collection, mis-rule it, or
 * lose it to a bad migration, and the person who has to fix it cannot sign in
 * to do so. The hardcoded owner is the way back.
 *
 * Only district addresses may be added. Enforced in the rules as well as here,
 * because this copy is advisory and the rules are not.
 */

export const OWNER_EMAIL = 'mdcallison@southfayette.org'

/** Access is limited to the district's domain. */
export const ALLOWED_EMAIL_DOMAIN = 'southfayette.org'

/** Firestore collection holding everyone who is not the owner. */
export const ALLOWED_USERS_COLLECTION = 'allowedUsers'

/**
 * The document id for an email address.
 *
 * The address *is* the id, which makes the rules check a single `exists()`
 * with no query — and no room for a near-match to slip through. Lowercased,
 * since Google addresses are case-insensitive but document ids are not.
 */
export function allowedUserDocId(email: string): string {
  return email.trim().toLowerCase()
}

/** Is this an address that could be added at all? */
export function isDistrictEmail(email: string): boolean {
  return allowedUserDocId(email).endsWith(`@${ALLOWED_EMAIL_DOMAIN}`)
}

export function isOwner(email: string | undefined | null): boolean {
  if (email === undefined || email === null) return false
  return allowedUserDocId(email) === OWNER_EMAIL
}

/**
 * An entry in `allowedUsers`, as the rules see it.
 *
 * `admin` is what lets someone else grant and revoke access. It exists because
 * the owner was the only person who could, which made every arrival and
 * departure wait on one person being reachable — fine for two users, not for
 * seven, and worst in August when nobody is at a desk.
 */
export interface AllowlistEntry {
  email: string
  admin: boolean
}

/**
 * May this person add and remove others?
 *
 * The owner always can, without a lookup — the same reasoning as everywhere
 * else here: the way back in cannot depend on the collection being intact.
 *
 * **Granting admin stays the owner's alone.** An admin who could appoint
 * admins is an admin who can hand out that power without the owner ever
 * seeing it, and there is no audit here to catch it. So: admins manage
 * ordinary users, the owner manages admins.
 */
export function canManageAccess(
  email: string | undefined | null,
  entries: readonly AllowlistEntry[] = [],
): boolean {
  if (email === undefined || email === null) return false
  const normalized = allowedUserDocId(email)
  if (normalized === OWNER_EMAIL) return true
  return entries.some(
    (entry) => allowedUserDocId(entry.email) === normalized && entry.admin,
  )
}

/** May this person's entry be edited or removed by `actor`? */
export function canManageEntry(
  actor: string | undefined | null,
  target: AllowlistEntry,
  entries: readonly AllowlistEntry[] = [],
): boolean {
  if (!canManageAccess(actor, entries)) return false
  // An admin cannot remove another admin — that is a peer, and a fight between
  // two of them should go to the owner rather than to whoever clicks first.
  return target.admin ? isOwner(actor) : true
}

/**
 * Both gates the rules apply: a verified address, and one that is either the
 * owner's or present in `allowedUsers`.
 *
 * `email_verified` matters. Without it a provider that does not verify
 * addresses could assert any address it liked, and the list would be checked
 * against a claim rather than a fact.
 *
 * `allowedEmails` is what the caller read from Firestore. Passing it in keeps
 * this function pure and testable; the I/O belongs to the caller.
 */
export function isAllowlistedEmail(
  email: string | undefined | null,
  emailVerified: boolean | undefined,
  allowedEmails: readonly string[] = [],
): boolean {
  if (emailVerified !== true) return false
  if (email === undefined || email === null) return false

  const normalized = allowedUserDocId(email)
  if (normalized === OWNER_EMAIL) return true
  return allowedEmails.map(allowedUserDocId).includes(normalized)
}
